import { useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from 'utils/cn';
import { motion } from 'framer-motion';

// Using Cloudinary URLs instead of local images for better performance
const about =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597871/oak-and-stone-remodel/about/about-rodney.webp';
const process =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597871/oak-and-stone-remodel/show/show18.webp';
const where =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597871/oak-and-stone-remodel/serviceArea/serviceArea.webp';

const Icons = {
  Team: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  ),
  Services: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  ),
  Location: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  )
};

export const Faqs = () => {
  const [currentFaq, setCurrentFaq] = useState(0);
  const [, setHoveredFaq] = useState(null);

  const faqImages = [about, process, where];

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const faqData = [
    {
      title: 'Who we are',
      subTitle: '35+ YEARS OF EXPERIENCE',
      text: 'We value our relationships with clients and love to make your dreams become your reality. We have a passion for the highest quality and perfection in our work.',
      link: '/about',
      buttonText: 'Learn more about our team',
      icon: Icons.Team
    },
    {
      title: 'What we do',
      text: 'We specialize in design, custom millwork, interior remodeling, additions and custom home building',
      link: '/gallery/showcase',
      buttonText: 'See our work',
      icon: Icons.Services
    },
    {
      title: 'Where we work',
      text: 'We work in the greater Wasatch Front area, covering Box Elder, Weber, Davis and Cache Counties.',
      link: '/service-area',
      buttonText: 'See what areas we cover',
      icon: Icons.Location
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="grid grid-cols-1 gap-8 items-center px-4 mx-auto max-w-7xl md:grid-cols-2 lg:gap-12 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-4 h-full md:order-2"
          role="tablist"
          aria-label="FAQ Categories">
          {faqData.map((item, index) => (
            <motion.div
              key={index}
              initial={false}
              animate={{
                scale: currentFaq === index ? 1 : 1,
                opacity: currentFaq === index ? 1 : 0.8
              }}
              whileHover={{ scale: 1.01 }}
              onClick={() => setCurrentFaq(index)}
              onMouseEnter={() => setHoveredFaq(index)}
              onMouseLeave={() => setHoveredFaq(null)}
              className={cn(
                'relative p-6 transition-all duration-200 cursor-pointer bg-white border-2',
                currentFaq === index
                  ? 'border-blue-600 shadow-sm'
                  : 'border-gray-200 hover:border-blue-400',
                'rounded-none'
              )}
              role="tab"
              tabIndex={currentFaq === index ? 0 : -1}
              aria-selected={currentFaq === index}
              aria-controls={`faq-panel-${index}`}
              id={`faq-tab-${index}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setCurrentFaq(index);
                  e.preventDefault();
                } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                  setCurrentFaq((prev) => (prev + 1) % faqData.length);
                  e.preventDefault();
                } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                  setCurrentFaq(
                    (prev) => (prev - 1 + faqData.length) % faqData.length
                  );
                  e.preventDefault();
                }
              }}>
              <div className="flex relative flex-col pl-10">
                <span
                  className={cn(
                    'absolute left-0 top-1 transition-colors duration-200',
                    currentFaq === index ? 'text-blue-600' : 'text-gray-500'
                  )}
                  aria-hidden="true">
                  {item.icon && <item.icon />}
                </span>
                <div className="flex-grow space-y-2">
                  <h2 className="text-xl font-semibold tracking-tight text-gray-900">
                    {item.title}
                  </h2>
                  {item.subTitle && (
                    <p className="text-xs font-bold tracking-wider text-blue-600">
                      {item.subTitle}
                    </p>
                  )}
                  <p className="text-sm leading-relaxed text-gray-600">
                    {item.text}
                  </p>
                </div>
                <motion.div
                  animate={{
                    opacity: currentFaq === index ? 1 : 0.7
                  }}
                  transition={{
                    duration: 0.2
                  }}
                  className="mt-4">
                  <Link
                    to={item.link}
                    onClick={(e) => {
                      e.stopPropagation();
                      scrollToTop();
                    }}
                    className={cn(
                      'inline-flex items-center text-sm font-semibold',
                      'transition-colors duration-200',
                      'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                      currentFaq === index
                        ? 'text-blue-600 hover:text-blue-700'
                        : 'text-gray-600 hover:text-blue-600'
                    )}>
                    {item.buttonText}
                    <svg
                      className={cn(
                        'ml-2 w-4 h-4 transition-transform duration-200',
                        currentFaq === index ? 'translate-x-1' : ''
                      )}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="relative aspect-[16/12] min-h-[350px] md:order-1 md:sticky md:top-16"
          role="tabpanel"
          id={`faq-panel-${currentFaq}`}
          aria-labelledby={`faq-tab-${currentFaq}`}>
          <div className="overflow-hidden absolute inset-0">
            {currentFaq === 2 ? (
              <iframe
                src="https://www.google.com/maps/d/u/0/embed?mid=1C9TRHcf1DcGlPQg2P__ebINF6VP_XJk&ehbc=2E312F"
                width="100%"
                height="100%"
                title="Oak and Stone Remodel Service Area Map"
                loading="lazy"
                style={{ border: 0 }}
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full rounded-xl">
                Your browser does not support iframes. Please visit our Google
                Maps page directly.
              </iframe>
            ) : (
              <motion.img
                key={currentFaq}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                src={faqImages[currentFaq]}
                alt={faqData[currentFaq].title}
                className="object-cover w-full h-full"
                loading="lazy"
              />
            )}
            <div
              className={`absolute inset-0 bg-gradient-to-b to-transparent from-black/10 ${
                currentFaq === 2 ? 'pointer-events-none' : ''
              }`}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
